import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import Modal from 'react-bootstrap/Modal';

import _get from 'lodash/get';
import _trim from 'lodash/trim';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlask } from '@fortawesome/free-solid-svg-icons';

import i18n from './messages.json';
import SendTestBody from './send-test/SendTestBody';
import SendTestFooter from './send-test/SendTestFooter';
import { isRecipientValid } from './send-test/recipient-validation';

const PHASE = {
  INPUT: 'input',
  SENDING: 'sending',
  RESULTS: 'results',
};

const compactTrim = (list) => list.map((v) => _trim(v)).filter(Boolean);

const SendTestDialog = (props) => {
  const { show, onHide, selectedCampaigns, onSubmit } = props;

  const [brazeRecipients, setBrazeRecipients] = useState(['']);
  const [phase, setPhase] = useState(PHASE.INPUT);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (!show) return;

    setBrazeRecipients(['']);
    setPhase(PHASE.INPUT);
    setResults(null);
  }, [show]);

  const hasCampaigns = Array.isArray(selectedCampaigns) && selectedCampaigns.length > 0;

  const trimmedBraze = useMemo(() => compactTrim(brazeRecipients), [brazeRecipients]);

  // Braze is the only test channel (WWW-15561 removed Firebase). The test targets
  // Braze User IDs (external_user_id) via /messages/send, which bypasses segment
  // membership. Each recipient may be a User ID or an email; require a segment to
  // be selected, at least one recipient, and that every one is well-formed so a
  // malformed email can't be submitted (emails are resolved to a User ID server-side).
  const canSubmit = useMemo(() => {
    if (phase !== PHASE.INPUT || !hasCampaigns) return false;
    return trimmedBraze.length > 0 && trimmedBraze.every(isRecipientValid);
  }, [phase, hasCampaigns, trimmedBraze]);

  const handleSubmit = useCallback(async () => {
    setPhase(PHASE.SENDING);

    try {
      const result = await onSubmit({ braze: { recipients: trimmedBraze } });

      setResults(result || { braze: null });
      setPhase(PHASE.RESULTS);
    } catch (error) {
      setResults({
        braze: null,
        fatalError: _get(error, 'message') || String(error),
      });
      setPhase(PHASE.RESULTS);
    }
  }, [onSubmit, trimmedBraze]);

  return (
    <Modal
      aria-labelledby='send-test-title'
      centered
      backdrop='static'
      onHide={onHide}
      show={show}
      className='send-test-modal'
      dialogClassName='send-test-dialog'
      backdropClassName='send-test-backdrop'
    >
      <Modal.Header closeButton>
        <Modal.Title id='send-test-title'>
          <FontAwesomeIcon icon={faFlask} className='send-test__title-icon' />
          {phase === PHASE.RESULTS
            ? i18n.ink.notification.sendTest.resultsTitle
            : i18n.ink.notification.sendTest.title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <SendTestBody
          phase={phase}
          results={results}
          hasCampaigns={hasCampaigns}
          brazeRecipients={brazeRecipients}
          onBrazeRecipientsChange={setBrazeRecipients}
        />
      </Modal.Body>

      <Modal.Footer>
        <SendTestFooter
          phase={phase}
          canSubmit={canSubmit}
          onHide={onHide}
          onSubmit={handleSubmit}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default memo(SendTestDialog);
